import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, Maximize2, Users, Ban } from 'lucide-react';
import { apiFetch, useAuth } from '../../../context/AuthContext';
import { useMiniChat } from '../context/MiniChatContext';
import MessagesContainer from './MessagesContainer';
import ChatInput from './ChatInput';
import MediaViewerModal from './MediaViewerModal';
import ForwardModal from './ForwardModal';
import useMessages from '../hooks/useMessages';
import useVoiceRecorder from '../hooks/useVoiceRecorder';
import useMediaUpload from '../hooks/useMediaUpload';
import { getMiniChatStyles } from '../style/miniChatStyles';
import { getAvatarColor, toOpaqueColor } from '../utils/formatters';
import { isSameUser, getFullInboxPath } from '../utils/userHelpers';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';
const WINDOW_WIDTH = 340;
const WINDOW_GAP = 12;
// The dock launcher is a 48px round button offset 16px from the edge —
// windows open right next to it, leaving it visible and clickable.
const DOCK_RAIL_WIDTH = 16 + 48 + 12;

// "Mohamed Ahmed Kamal Zaki" -> "Mohamed Ahmed" — matches the dock rail's label.
const firstTwoWords = (name) => {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).join(' ');
};

/**
 * One floating panel per open window. Has a single compact header of its
 * own (avatar + name + status + minimize/close) — deliberately NOT the full
 * ChatHeader component, which would stack a second, redundant header below
 * it and duplicate the contact name. MessagesContainer / ChatInput ARE
 * still reused exactly as the full messenger page uses them, fed through
 * `getMiniChatStyles`. Owns its OWN useMessages() instance (real
 * `selectedUser`, local input/scroll state) — see MiniChatContext.jsx for
 * why the provider-level instance can't be reused for compose/send.
 */
const ChatWindow = ({ chatId, minimized, index }) => {
  const {
    conversations, messages, setMessages, setConversations, onlineUsers, employeeProfilePics,
    currentUser, typingUsers, socket, isConnected, c, isRTL, t, theme, language,
    closeChat, setAlertState, isGroupChatId, chatPrefs, setSharedSelectedUser,
  } = useMiniChat();

  const { hasFeatureAccess } = useAuth();

  // ── Admin-level feature gating (registry page "messages") ────────────────
  // Same idiom as MessengerPage/MiniChatDock: only constrains promoted users
  // in Admin View; hasFeatureAccess bypasses for admin/superadmin roles.
  const windowViewType =
    localStorage.getItem('managerViewType') ||
    localStorage.getItem('viewType') ||
    'user';
  const isAdminViewGated = windowViewType === 'admin';
  const canSendMessages = !isAdminViewGated || hasFeatureAccess('messages', 'send');
  const canDeleteMessages = !isAdminViewGated || hasFeatureAccess('messages', 'remove');
  const canReplyMessages = !isAdminViewGated || hasFeatureAccess('messages', 'reply');
  const canReactMessages = !isAdminViewGated || hasFeatureAccess('messages', 'react');
  const canForwardMessages = !isAdminViewGated || hasFeatureAccess('messages', 'forward');
  const canViewMessageDetails = !isAdminViewGated || hasFeatureAccess('messages', 'viewDetails');
  const canStarMessages = !isAdminViewGated || hasFeatureAccess('messages', 'starredMessages');
  const canBlockUsers = !isAdminViewGated || hasFeatureAccess('messages', 'block');

  const [messageInput, setMessageInput] = useState('');
  const [viewerState, setViewerState] = useState(null); // { message }
  const [replyingTo, setReplyingTo] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const fileInputRef = useRef(null);
  const styles = useMemo(() => getMiniChatStyles(c, isRTL, theme), [c, isRTL, theme]);
  // The app's own messenger design tokens — `popoverBg` is the system's
  // existing floating-surface color (already used for dropdowns/popovers
  // elsewhere), reused here instead of a one-off palette. It's only 95%
  // opaque by design (for the full page's glass effect); `toOpaqueColor`
  // strips that alpha so this floating window is never see-through.
  const solidBg = toOpaqueColor(c.popoverBg);
  const solidHeaderBg = solidBg;
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const navigate = useNavigate();
  const conversation = conversations.find((cv) => String(cv.id) === String(chatId));
  const goToFullInbox = () => {
    // Pre-select this exact conversation so the full Messenger page opens
    // straight into it instead of an empty inbox — it reads this same
    // shared context value on mount (see MessengerPage.jsx's `_userSelected`
    // restore check).
    if (conversation) {
      setSharedSelectedUser({ ...conversation, _userSelected: true });
    }
    navigate(getFullInboxPath());
    closeChat(chatId);
  };

  const messagesState = useMessages({
    socket, isConnected, currentUser, selectedUser: conversation,
    messages, setMessages, conversations, setConversations,
    allEmployees: [], userNamesCache: {},
    onlineUsers, messageInput, setMessageInput,
    t, setAlertState,
    replyingTo,
    clearReply: () => setReplyingTo(null),
  });

  // Bring the window's focus back to the latest message whenever it's
  // opened/restored — clicking an already-open conversation in the dock
  // should feel like it was "brought to front", not ignored.
  useEffect(() => {
    if (minimized) return;
    const id = requestAnimationFrame(() => {
      messagesState.messagesEndRef?.current?.scrollIntoView({ block: 'end' });
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minimized, chatId]);

  const voiceState = useVoiceRecorder(t);
  const mediaState = useMediaUpload({
    selectedUser: conversation,
    isConnected,
    messageInput, setMessageInput,
    setMessages, setConversations,
    currentUser, onlineUsers, socket, t,
  });

  const handleForwardConfirm = async (targets) => {
    if (!forwardMsg) return;
    try {
      await messagesState.forwardMessage(forwardMsg, targets);
      setForwardMsg(null);
    } catch (err) {
      console.error('Forward failed:', err);
      setAlertState({ open: true, title: t.error || t.errorTitle, message: t.sendFailed, variant: 'error' });
    }
  };

  // Mirrors MessengerPage.jsx's own page-local `sendRecording` — never
  // factored out as a reusable export, so replicated here rather than
  // reaching into that page's internals.
  const sendRecording = async () => {
    if (!voiceState.audioBlob || !conversation) return;
    try {
      const voiceFile = new File([voiceState.audioBlob], `voice_${Date.now()}.webm`, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', voiceFile);
      const uploadRes = await apiFetch(`${API_BASE_URL}/api/private-messages/upload/audio`, {
        method: 'POST',
        body: formData,
      });
      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        throw new Error(err.error || 'Voice upload failed');
      }
      const { data: uploaded } = await uploadRes.json();
      const mediaData = {
        storageKey: uploaded.storageKey,
        url: uploaded.url,
        type: 'voice',
        name: uploaded.name || voiceFile.name,
        size: uploaded.size || voiceState.audioBlob.size,
        duration: voiceState.recordingTime,
        mimeType: 'audio/webm',
      };
      await mediaState.sendMediaMessage(mediaData, 'voice');
      voiceState.setAudioBlob(null);
    } catch (err) {
      console.error('Failed to send voice:', err);
      setAlertState({ open: true, title: t.error, message: t.sendFailed, variant: 'error' });
    }
  };

  const handleDeleteWithConfirm = (message) => {
    setAlertState({
      open: true,
      title: isRTL ? 'حذف الرسالة' : 'Delete Message',
      message: isRTL ? 'هل أنت متأكد من حذف هذه الرسالة؟' : 'Are you sure you want to delete this message?',
      variant: 'error',
      showCancel: true,
      onConfirm: () => {
        setAlertState((prev) => ({ ...prev, open: false }));
        messagesState.handleDeleteMessage(message);
      },
    });
  };

  // Snapshot stored in localStorage for the Starred Messages page — mirrors
  // MessengerPage.jsx's own buildStarSnapshot exactly. Both read/write the
  // SAME chatPrefs instance (lifted to MessengerContext), so starring here
  // shows up instantly in the full Messenger and vice versa.
  const buildStarSnapshot = (message) => ({
    id: message.id,
    chatId: conversation?.id,
    chatName: chatPrefs.getAlias(conversation?.id) || conversation?.name,
    text: message.text || null,
    type: message.type || 'text',
    mediaName: message.mediaName || null,
    senderName: message.sent ? null : (message.sender?.name || conversation?.name),
    sent: !!message.sent,
    time: message.time,
  });

  if (!conversation) return null;

  const isGroup = isGroupChatId(chatId) || conversation.isGroup;
  // Messenger Phase 1: mirrors MessengerPage.jsx's isCurrentUserAdminOfSelectedGroup —
  // needed here too since the Mini Chat Dock is a second, independent composer
  // render site for the same ADMINS_ONLY gating.
  const isCurrentUserAdmin = isGroup && conversation.members?.some(
    (m) => isSameUser(m.id, currentUser?.infinityCode) && m.role === 'admin'
  );
  const postingModeBlocksMe = isGroup && conversation.postingMode === 'ADMINS_ONLY' && !isCurrentUserAdmin;
  const isUserOnline = !isGroup && Array.from(onlineUsers || []).some((id) => isSameUser(id, chatId));
  const isTyping = !isGroup && typingUsers.has(String(chatId));
  const currentChatMessages = messages[String(chatId)] || messages[chatId] || [];
  const chatImages = currentChatMessages.filter((m) => m.type === 'image' && m.mediaUrl);
  const viewerImageIndex = viewerState?.message?.type === 'image'
    ? chatImages.findIndex((m) => String(m.id) === String(viewerState.message.id))
    : -1;

  // On phones the fixed 340px window + per-index tiling would push windows
  // off-screen — clamp the width to the viewport and stack windows in place
  // (highest index on top) instead of tiling sideways.
  const isNarrowViewport = typeof window !== 'undefined' && window.innerWidth <= 768;
  const offset = isNarrowViewport
    ? DOCK_RAIL_WIDTH
    : DOCK_RAIL_WIDTH + index * (WINDOW_WIDTH + WINDOW_GAP);
  const positionStyle = isRTL
    ? { left: offset, right: 'auto' }
    : { right: offset, left: 'auto' };

  const label = firstTwoWords(conversation.englishName || conversation.name);
  const statusText = isTyping ? t.typing : (isGroup ? '' : (isUserOnline ? t.activeNow : t.offline));

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        ...positionStyle,
        width: isNarrowViewport
          ? `min(${WINDOW_WIDTH}px, calc(100vw - ${DOCK_RAIL_WIDTH + 12}px))`
          : WINDOW_WIDTH,
        height: minimized ? 'auto' : 'min(440px, 70dvh)',
        background: solidBg,
        border: `1px solid ${c.border}`,
        borderBottom: 'none',
        borderRadius: '12px 12px 0 0',
        boxShadow: c.shadow,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 900 - index,
        overflow: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {/* Single compact header — avatar, name (first two words), status,
          open-full-inbox/close. No separate ChatHeader here on purpose:
          stacking it below this would just repeat the same name in a
          second row. No minimize-to-a-named-bar behavior (removed) — the
          Back button is the only way to collapse this back down. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 10px', background: solidHeaderBg,
          borderBottom: minimized ? 'none' : `1px solid ${c.border}`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
          {/* Back — closes this mini chat and restores whichever the dock
              was showing before (launcher pill or conversation list), since
              closing the window is all that's needed for that to reappear. */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeChat(chatId); }}
            title={isRTL ? 'رجوع' : 'Back'}
            aria-label={isRTL ? 'رجوع' : 'Back'}
            style={{
              border: 'none', background: 'transparent', color: c.textSecondary,
              cursor: 'pointer', display: 'flex', padding: '4px', flexShrink: 0,
              marginInlineStart: '-4px',
            }}
          >
            <BackIcon size={16} />
          </button>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {employeeProfilePics[chatId] ? (
              <img
                src={employeeProfilePics[chatId]}
                alt={label}
                style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
              />
            ) : null}
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              display: employeeProfilePics[chatId] ? 'none' : 'flex',
              alignItems: 'center', justifyContent: 'center',
              background: getAvatarColor(conversation.name), color: '#fff', fontSize: '12px', fontWeight: 700,
            }}
            >
              {isGroup ? <Users size={14} /> : conversation.avatar}
            </div>
            {isUserOnline && (
              <div style={{
                position: 'absolute', bottom: 0, insetInlineEnd: 0, width: 9, height: 9,
                borderRadius: '50%', background: c.online || '#22c55e', border: `2px solid ${solidHeaderBg}`,
              }}
              />
            )}
          </div>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </span>
            {!minimized && statusText && (
              <span style={{ fontSize: '11px', color: isTyping ? '#2563eb' : c.textSecondary, fontStyle: isTyping ? 'italic' : 'normal' }}>
                {statusText}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={goToFullInbox}
            title={isRTL ? 'فتح صفحة الرسائل' : 'Open full inbox'}
            aria-label={isRTL ? 'فتح صفحة الرسائل' : 'Open full inbox'}
            style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', display: 'flex', padding: '4px' }}
          >
            <Maximize2 size={14} />
          </button>
          <button
            type="button"
            onClick={() => closeChat(chatId)}
            title={t.miniChatClose}
            aria-label={t.miniChatClose}
            style={{ border: 'none', background: 'transparent', color: c.textSecondary, cursor: 'pointer', display: 'flex', padding: '4px' }}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          <MessagesContainer
            messages={messages}
            selectedUser={conversation}
            hasMoreMessages={messagesState.hasMoreMessages}
            messagesOffset={messagesState.messagesOffset}
            loadingMoreFor={messagesState.loadingMoreFor}
            loadMoreMessages={messagesState.loadMoreMessages}
            isRTL={isRTL}
            c={c}
            employeeProfilePics={employeeProfilePics}
            getAvatarColor={getAvatarColor}
            styles={styles}
            className="mini-chat-scroll"
            compact
            hideAvatar
            messagesContainerRef={messagesState.messagesContainerRef}
            messagesEndRef={messagesState.messagesEndRef}
            userAtBottomRef={messagesState.userAtBottomRef}
            t={t}
            language={language}
            theme={theme}
            onDeleteMessage={canDeleteMessages ? handleDeleteWithConfirm : undefined}
            openMediaViewer={(message) => setViewerState({ message })}
            onReply={canReplyMessages ? (message) => setReplyingTo(message) : undefined}
            onForward={canForwardMessages ? (message) => setForwardMsg(message) : undefined}
            onToggleStar={canStarMessages ? (message) => chatPrefs.toggleStarMessage(buildStarSnapshot(message)) : undefined}
            isStarredFn={(messageId) => chatPrefs.isStarred(messageId)}
            onReact={canReactMessages ? messagesState.toggleReaction : undefined}
            canViewDetails={canViewMessageDetails}
            currentUserCode={currentUser?.infinityCode}
            resolveReactorName={(code) => (
              isSameUser(code, currentUser?.infinityCode) ? t.you : conversation.name
            )}
          />

          {chatPrefs.isBlocked(conversation.id) ? (
            // Mirrors MessengerPage's blocked banner — the mini window must
            // never offer a compose box for a blocked chat.
            <div style={{
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              color: c.textSecondary,
              background: c.inputBg,
              borderTop: `1px solid ${c.border}`,
              fontSize: '13px',
              fontWeight: '600',
              flexWrap: 'wrap',
              textAlign: 'center',
            }}>
              <Ban size={14} color="#ef4444" />
              {t.blockedBanner}
              {canBlockUsers && (
              <button
                type="button"
                onClick={() => chatPrefs.toggleBlocked(conversation.id)}
                style={{
                  border: '1px solid #2e7cba', background: 'transparent', color: '#2e7cba',
                  borderRadius: '10px', padding: '4px 12px', cursor: 'pointer', fontWeight: '700', fontSize: '12px',
                }}
              >
                {t.unblockAction}
              </button>
              )}
            </div>
          ) : canSendMessages && postingModeBlocksMe ? (
            <div style={{
              padding: '12px',
              textAlign: 'center',
              color: c.textSecondary,
              background: c.inputBg,
              borderTop: `1px solid ${c.border}`,
              fontSize: '12.5px',
              fontWeight: '600'
            }}>
              {t.adminsOnlyComposerNote}
            </div>
          ) : canSendMessages && (
          <ChatInput
            messageInput={messageInput}
            setMessageInput={setMessageInput}
            handleSendMessage={messagesState.handleSendMessage}
            handleTyping={messagesState.handleTyping}
            handleKeyPress={messagesState.handleKeyPress}
            handleFileSelect={mediaState.handleFileSelect}
            isRecording={voiceState.isRecording}
            audioBlob={voiceState.audioBlob}
            recordingTime={voiceState.recordingTime}
            startRecording={voiceState.startRecording}
            stopRecording={voiceState.stopRecording}
            sendRecording={sendRecording}
            cancelRecording={voiceState.cancelRecording}
            uploading={mediaState.uploading}
            isConnected={isConnected}
            fileInputRef={fileInputRef}
            isMobile={false}
            styles={styles}
            textareaClassName="mini-chat-scroll"
            c={c}
            t={t}
            isRTL={isRTL}
            theme={theme}
            replyingTo={replyingTo}
            onCancelReply={() => setReplyingTo(null)}
            selectedUserName={conversation.name}
          />
          )}
        </>
      )}

      {viewerState?.message && (
        <MediaViewerModal
          mediaUrl={viewerState.message.mediaUrl}
          mediaType={viewerState.message.type}
          mediaName={viewerState.message.mediaName}
          onClose={() => setViewerState(null)}
          isRTL={isRTL}
          t={t}
          images={viewerImageIndex >= 0 ? chatImages : null}
          currentIndex={viewerImageIndex}
          onNavigate={(newIndex) => setViewerState({ message: chatImages[newIndex] })}
          message={viewerState.message}
          onReact={canReactMessages ? messagesState.toggleReaction : undefined}
        />
      )}

      {forwardMsg && (
        <ForwardModal
          conversations={conversations}
          onClose={() => setForwardMsg(null)}
          onForward={handleForwardConfirm}
          c={c}
          t={t}
          isRTL={isRTL}
          theme={theme}
          getAvatarColor={getAvatarColor}
          employeeProfilePics={employeeProfilePics}
        />
      )}
    </div>
  );
};

export default ChatWindow;
